import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Home, Users, ArrowLeft, X, Check, Pen } from "lucide-react";
import { toast } from "sonner";

const blankUnit = {
  name: "",
  type: "Room",
  capacity: 2,
  amenities: "",
  notes: "",
  active: true,
};

export default function HousingUnits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const isAdmin = ["admin", "global_admin"].includes(user.role);
  const canManage = ["admin", "global_admin", "hospitality_coordinator", "booking_coordinator"].includes(user.role);

  const load = useCallback(async () => {
    try {
      const u = await api.get("/housing/units");
      setUnits(u.data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveUnit = async () => {
    if (!editing.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      const payload = { ...editing, capacity: Number(editing.capacity) || 1 };
      if (editing.id) {
        await api.put(`/housing/units/${editing.id}`, payload);
        toast.success("Unit updated");
      } else {
        await api.post("/housing/units", payload);
        toast.success("Unit created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteUnit = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}? Existing bookings stay intact, but it won't be assignable to new ones.`)) return;
    try {
      await api.delete(`/housing/units/${u.id}`);
      toast.success("Unit deactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="housing-units-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58] hover:text-[#1C1F1E]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Housing Units
          </h1>
          <p className="text-[#525C58] mt-1">Manage rooms, cottages and houses available to visitors.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <Button variant="outline" onClick={() => navigate("/rate-cards")} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="manage-rate-cards-btn">
              <Pen className="h-4 w-4 mr-2" /> Manage rate cards
            </Button>
          )}
          {canManage && (
            <Button onClick={() => setEditing({ ...blankUnit })} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-unit-btn">
              <Plus className="h-4 w-4 mr-2" /> New unit
            </Button>
          )}
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Home className="h-5 w-5 text-[#40916C]" /> Inventory ({units.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-[#7A8A84]">Loading…</div>
          ) : units.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-10">No housing units yet. Click "New unit" to add your first.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="units-grid">
              {units.map(u => (
                <div key={u.id} className={`p-5 rounded-xl border ${u.active ? "border-[#E8E8E6] bg-white" : "border-red-100 bg-red-50/40 opacity-70"}`} data-testid={`unit-card-${u.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-semibold text-[#1C1F1E]" style={{ fontFamily: "Manrope, sans-serif" }}>{u.name}</h3>
                      <p className="text-xs text-[#7A8A84]">{u.type} · capacity {u.capacity}</p>
                    </div>
                    {!u.active && <Badge className="bg-red-100 text-red-800 border-0 text-[10px]">Inactive</Badge>}
                  </div>
                  {u.amenities && <p className="text-xs text-[#525C58] mb-3 line-clamp-2">{u.amenities}</p>}
                  <div className="flex gap-2 pt-2 border-t border-[#E8E8E6]">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ ...u, rates_kes: u.rates_kes || {}, rates_usd: u.rates_usd || {} })} className="text-[#1B4332] flex-1" data-testid={`edit-unit-${u.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    {isAdmin && u.active && (
                      <Button size="sm" variant="ghost" onClick={() => deleteUnit(u)} className="text-red-600 hover:bg-red-50" data-testid={`delete-unit-${u.id}`}>
                        Deactivate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / create unit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit unit" : "New housing unit"}</DialogTitle>
            <DialogDescription>Set capacity, amenities and a nightly rate per visitor type.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Guest House A" data-testid="unit-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Input value={editing.type || ""} onChange={(e) => setEditing({ ...editing, type: e.target.value })} placeholder="Room / Cottage / House" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Capacity</Label>
                  <Input type="number" min={1} value={editing.capacity} onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} />
                </div>
                <div className="flex items-center gap-2 pt-7">
                  <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                  <Label>Active</Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Amenities</Label>
                <Input value={editing.amenities || ""} onChange={(e) => setEditing({ ...editing, amenities: e.target.value })} placeholder="Kitchen, WiFi, hot water, etc." />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <div className="rounded-md bg-[#F4F4F1] border border-[#E8E8E6] p-3 text-xs text-[#525C58]">
                Nightly rates are managed centrally as <strong>Rate Cards</strong>. They are picked per booking segment, not per unit.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveUnit} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-unit-btn">
              <Check className="h-4 w-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
